import { z } from 'zod';
import { operationLifecycleSchema } from '../status/operation.js';
import { operationKindSchema, renewalDiscountStateSchema } from '../status/renewal-discount.js';
import { renewalDiscountDetailSchema } from './renewal-discount.js';
import { storeCodeSchema } from '../status/store.js';
import { decimalStringSchema, isoDateTimeSchema, objectIdSchema, paginationQuerySchema, sortQuerySchema, queryBooleanSchema, } from './common.js';
/**
 * Opérations commerciales (`01` §7, `02` §10) après la refonte du 10/09/2026 :
 * une seule action possible (**ajouter des produits**, un ou plusieurs SKU
 * depuis le 15/10/2026), des conditions lisibles (canal, SKU présent, montant,
 * code promo) et une fin de période facultative.
 */
/**
 * Période d'application (`01` §7.1) — bornes incluses, fuseau `Europe/Paris`.
 * `end: null` = « sans date de fin » : l'opération court tant qu'elle est
 * active.
 */
export const operationPeriodSchema = z.object({
    start: isoDateTimeSchema,
    end: isoDateTimeSchema.nullable(),
    timezone: z.string().default('Europe/Paris'),
});
export const operationConditionsSchema = z.object({
    /**
     * Canaux (`source_name` Shopify) acceptés. Vide = tous. Un suffixe `*`
     * couvre une famille (`subscription_*`).
     */
    channels: z.array(z.string().min(1)),
    /** La commande doit contenir au moins un de ces SKU (bundles expansés compris). */
    skus: z.array(z.string().min(1)),
    /** Montant TTC minimum ; `0` = pas de plancher. */
    minAmount: decimalStringSchema,
    /** Montant TTC maximum ; `null` = pas de plafond. `'0'` accepte un total nul. */
    maxAmount: decimalStringSchema.nullable(),
    /** La commande doit porter au moins un de ces codes (insensible à la casse). */
    discountCodes: z.array(z.string().min(1)),
});
/** Produit ajouté : une ligne du CSV envoyé à Martelet. */
export const operationProductSchema = z.object({
    sku: z.string().min(1),
    qty: z.number().int().positive(),
    unitPrice: decimalStringSchema,
});
/** Au moins un produit, chaque SKU une seule fois (sa ligne CSV porte le SKU dans sa référence). */
export const operationProductsSchema = z
    .array(operationProductSchema)
    .min(1)
    .refine((products) => new Set(products.map((product) => product.sku)).size === products.length, {
    message: 'Chaque SKU ne peut être ajouté qu’une fois.',
});
export const operationListItemSchema = z.object({
    id: objectIdSchema,
    /** Sorte d'opération (`07` §1). */
    kind: operationKindSchema,
    name: z.string(),
    enabled: z.boolean(),
    archived: z.boolean(),
    period: operationPeriodSchema,
    /** Vide = toutes les origines. Le champ garde son nom historique. */
    stores: z.array(storeCodeSchema),
    lifecycle: operationLifecycleSchema,
    /** État propre d'une remise renouvellement (`07` §4.3) ; `null` pour un produit offert. */
    renewalState: renewalDiscountStateSchema.nullable(),
    /** Nombre total de commandes auxquelles l'opération a été appliquée (`02` §10). */
    matchedOrdersCount: z.number().int().min(0),
    updatedAt: isoDateTimeSchema,
    updatedBy: objectIdSchema.nullable(),
});
export const operationDetailSchema = z.object({
    ...operationListItemSchema.shape,
    /** Opérations « produit offert » ; neutres (`[]`, aucune condition) pour une remise renouvellement. */
    conditions: operationConditionsSchema,
    products: z.array(operationProductSchema),
    /** Présent ssi `kind = renewal_discount`. */
    renewalDiscount: renewalDiscountDetailSchema.nullable(),
});
export const operationListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
    ...sortQuerySchema.shape,
    /** Défaut `false` : les archivées sont masquées tant qu'on ne les demande pas. */
    archived: queryBooleanSchema.optional(),
    lifecycle: operationLifecycleSchema.optional(),
    /** État propre d'une remise renouvellement (Brouillon, Activée…) : c'est lui que la liste affiche pour ce type. */
    renewalState: renewalDiscountStateSchema.optional(),
    kind: operationKindSchema.optional(),
    search: z.string().min(1).optional(),
});
/** Opération « produit offert » ; `kind` absent = `free_product` (clients antérieurs à `07`). */
export const operationInputSchema = z.object({
    kind: z.literal('free_product').optional(),
    name: z.string().min(1),
    enabled: z.boolean().default(true),
    period: operationPeriodSchema,
    /** Vide = toutes les origines (`01` §7.1). */
    stores: z.array(storeCodeSchema).default([]),
    conditions: operationConditionsSchema,
    products: operationProductsSchema,
});
/** Prévisualisation sur les 30 derniers jours (`01` §7.3) — aucun appel Shopify. */
export const operationPreviewItemSchema = z.object({
    orderId: objectIdSchema,
    name: z.string(),
    store: storeCodeSchema,
    paidAt: isoDateTimeSchema.nullable(),
    totalInclTax: decimalStringSchema.nullable(),
});
export const operationPreviewSchema = z.object({
    total: z.number().int().min(0),
    sample: z.array(operationPreviewItemSchema),
});
/**
 * Corps du bouton « Prévisualiser » (`01` §7.3, `02` §10) : évalué sur les
 * conditions du formulaire, qu'elles soient déjà enregistrées ou non — la
 * période n'entre pas en jeu (`domain/operations` `previewOperations`).
 */
export const operationPreviewBodySchema = z.object({
    conditions: operationConditionsSchema,
    /** Vide = toutes les origines. */
    stores: z.array(storeCodeSchema).default([]),
});
/** Commande ayant reçu une opération (`02` §10, onglet « Commandes »). */
export const operationOrderRefSchema = z.object({
    orderId: objectIdSchema,
    name: z.string(),
    store: storeCodeSchema,
    sentAt: isoDateTimeSchema.nullable(),
});
/** `GET /api/operations/:id/orders` — liste paginée (`02` §10). */
export const operationOrdersQuerySchema = z.object({
    ...paginationQuerySchema.shape,
});
//# sourceMappingURL=operations.js.map